import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import resolveDocumentStateSeverity from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/resolveDocumentStateSeverity';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type WorkflowRunSummaryView from '@UI/Store/WorkflowRuns/Views/WorkflowRunSummaryView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tag } from '@verclam/ui-kit';

/**
 * Three cells on the list's grid (title and opening date, state tag, action) and, after them, the chips
 * row spanning the full width.
 */
const cardStyle = css`
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: center;
	row-gap: 1rem;
	padding: 1.375rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		row-gap: 0.75rem;
	}
`;

const titleBlockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	min-width: 0;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	font-size: 1.25rem;
	color: ${theme.colors.ink};
`;

const subtitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const chipsStyle = css`
	grid-column: 1 / -1;
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunSummaryView;
	readonly documentTypes: DocumentTypesListView | null;
	/** Whether the visitor may continue the preparation; otherwise the same button only opens it for reading. */
	readonly canContinue: boolean;
};

const describeProgress = (preparation: WorkflowRunSummaryView): string => {
	if (preparation.isOpen) {
		return 'À compléter';
	}

	if (preparation.isGenerating) {
		return 'Génération en cours';
	}

	const toReviewCount = preparation.documents.filter((document) => document.isToReview).length;
	const failedCount = preparation.documents.filter((document) => document.state.value === 'failed').length;
	const parts: string[] = [];

	if (toReviewCount > 0) {
		parts.push(toReviewCount === 1 ? '1 document à relire' : `${toReviewCount} documents à relire`);
	}

	if (failedCount > 0) {
		parts.push(failedCount === 1 ? '1 document en échec' : `${failedCount} documents en échec`);
	}

	return parts.length > 0 ? parts.join(' · ') : 'Génération terminée';
};

/**
 * One preparation still being assembled, generated or reviewed. Once generation has been launched the
 * card also lists each document with its state; before that every document would read "en attente",
 * which says nothing.
 */
const OpenPreparationCard: React.FC<Props> = ({ subject, preparation, documentTypes, canContinue }) => {
	const navigate = useNavigate();

	const handleOpenClick = React.useCallback(() => {
		navigate(`${buildSubjectPath(subject)}/preparations/${preparation.id}`);
	}, [navigate, subject, preparation.id]);

	return (
		<div css={cardStyle}>
			<div css={titleBlockStyle}>
				<h3 css={titleStyle}>{preparation.label}</h3>
				<p css={subtitleStyle}>
					Ouverte le {formatLongDate(preparation.openedAt.toISOString())} ·{' '}
					{describeProgress(preparation)}
				</p>
			</div>
			<div>
				<Tag rounded value={preparation.state.label} />
			</div>
			<div>
				<Button
					type="button"
					label={canContinue ? 'Reprendre' : 'Consulter'}
					onClick={handleOpenClick}
				/>
			</div>
			<ConditionalLoader data={!preparation.isOpen && preparation.documents.length > 0}>
				<div css={chipsStyle}>
					{preparation.documents.map((document) => (
						<Tag
							key={document.documentTypeId}
							rounded
							severity={resolveDocumentStateSeverity(document.state.value)}
							value={`${documentTypes?.labelOf(document.documentTypeId) ?? 'Document'} · ${document.state.label}`}
						/>
					))}
				</div>
			</ConditionalLoader>
		</div>
	);
};

export default OpenPreparationCard;
